import { fetchAuth } from "../../../shared/api/ApiService";
import { IDeleteStudentRegularScheduleRequest, IDeleteStudentRegularScheduleResponse,
    IGetStudentRegularScheduleResponse, IGetTutorAvailabilityRequest, IGetTutorAvailabilityResponse,
    IOccupiedSlot,
    IPreviewStudentRegularScheduleRequest, IPreviewStudentRegularScheduleResponse, IUpdateStudentRegularScheduleRequest,
    IUpdateStudentRegularScheduleResponse, IUpdateTutorAvailabilityRequest, IUpdateTutorAvailabilityResponse,
} from "../model/types";

type TApiSlotPattern = {
    weekday?: string;
    startSlot?: number;
    start_slot?: number;
    durationSlots?: number;
    duration_slots?: number;
}

type TApiOccupiedSlot = {
    slotPattern?: TApiSlotPattern;
    slot_pattern?: TApiSlotPattern;
    tag?: {
        id?: string;
        name?: string;
    };
    student?: {
        id?: string;
        userId?: string;
        user_id?: string;
        fullName?: string;
        full_name?: string;
    };
    parent?: string;
}

type TApiTutorAvailability = {
    userId?: string;
    user_id?: string;
    weeklySlots?: TApiSlotPattern[];
    weekly_slots?: TApiSlotPattern[];
    occupiedSlots?: TApiSlotPattern[];
    occupied_slots?: TApiSlotPattern[];
    occupiedSlotsV2?: TApiOccupiedSlot[];
    occupied_slots_v2?: TApiOccupiedSlot[];
    dateOverrides?: IGetTutorAvailabilityResponse['availability']['dateOverrides'];
    date_overrides?: IGetTutorAvailabilityResponse['availability']['dateOverrides'];
    availableActions?: IGetTutorAvailabilityResponse['availability']['availableActions'];
    available_actions?: IGetTutorAvailabilityResponse['availability']['availableActions'];
    audit?: IGetTutorAvailabilityResponse['availability']['audit'];
}

type TApiDateOverride = {
    date?: IGetTutorAvailabilityResponse['availability']['dateOverrides'][number]['date'];
    availableSlots?: IGetTutorAvailabilityResponse['availability']['dateOverrides'][number]['availableSlots'];
    available_slots?: IGetTutorAvailabilityResponse['availability']['dateOverrides'][number]['availableSlots'];
    comment?: string;
}

const mapSlotPattern = (slot?: TApiSlotPattern) => {
    if(!slot?.weekday) return null

    return {
        weekday: slot.weekday as IGetTutorAvailabilityResponse['availability']['weeklySlots'][number]['weekday'],
        startSlot: slot.startSlot ?? slot.start_slot ?? 0,
        durationSlots: slot.durationSlots ?? slot.duration_slots ?? 0,
    }
}

const mapOccupiedSlot = (slot: TApiOccupiedSlot): IOccupiedSlot | null => {
    const slotPattern = mapSlotPattern(slot.slotPattern ?? slot.slot_pattern)
    if(!slotPattern) return null

    return {
        ...slotPattern,
        tag: slot.tag?.id || slot.tag?.name ? {
            id: slot.tag?.id ?? '',
            name: slot.tag?.name ?? '',
        } : undefined,
        student: slot.student?.id || slot.student?.fullName || slot.student?.full_name ? {
            id: slot.student?.id ?? '',
            userId: slot.student?.userId ?? slot.student?.user_id ?? '',
            fullName: slot.student?.fullName ?? slot.student?.full_name ?? '',
        } : undefined,
        parent: slot.parent ?? '',
    }
}

const mapDateOverride = (override: TApiDateOverride): IGetTutorAvailabilityResponse['availability']['dateOverrides'][number] | null => {
    if(!override.date) return null

    return {
        date: override.date,
        availableSlots: override.availableSlots ?? override.available_slots ?? [],
        comment: override.comment ?? '',
    }
}

const mapTutorAvailability = (availability: TApiTutorAvailability): IGetTutorAvailabilityResponse['availability'] => {
    const weeklySlotsSource = availability.weeklySlots ?? availability.weekly_slots ?? []
    const occupiedSlotsV2Source = availability.occupiedSlotsV2 ?? availability.occupied_slots_v2 ?? []
    const fallbackOccupiedSlotsSource = availability.occupiedSlots ?? availability.occupied_slots ?? []
    const dateOverridesSource = availability.dateOverrides ?? availability.date_overrides ?? []

    return {
        userId: availability.userId ?? availability.user_id ?? '',
        weeklySlots: weeklySlotsSource
            .map(mapSlotPattern)
            .filter((slot): slot is NonNullable<typeof slot> => Boolean(slot)),
        occupiedSlots: occupiedSlotsV2Source.length
            ? occupiedSlotsV2Source
                .map(mapOccupiedSlot)
                .filter((slot): slot is IOccupiedSlot => Boolean(slot))
            : fallbackOccupiedSlotsSource
                .map(mapSlotPattern)
                .filter((slot): slot is NonNullable<typeof slot> => Boolean(slot)),
        dateOverrides: dateOverridesSource
            .map(mapDateOverride)
            .filter((override): override is NonNullable<typeof override> => Boolean(override)),
        availableActions: availability.availableActions ?? availability.available_actions ?? [],
        audit: availability.audit,
    }
}

class ScheduleService {

    async getTutorAvailability(data: IGetTutorAvailabilityRequest): Promise<IGetTutorAvailabilityResponse> {
        const searchParams = new URLSearchParams()

        if(data.from){
            searchParams.set('from.year', String(data.from.year))
            searchParams.set('from.month', String(data.from.month))
            searchParams.set('from.day', String(data.from.day))
        }

        if(data.to){
            searchParams.set('to.year', String(data.to.year))
            searchParams.set('to.month', String(data.to.month))
            searchParams.set('to.day', String(data.to.day))
        }

        const query = searchParams.toString()
        const res = await fetchAuth(
            `${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/schedules/tutors/${data.userId}/availability${query ? `?${query}` : ''}`
        )

        const response = await res.json()

        return {
            ...response,
            availability: mapTutorAvailability(response.availability ?? {}),
        }
    }

    async updateTutorAvailability(data: IUpdateTutorAvailabilityRequest): Promise<IUpdateTutorAvailabilityResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/schedules/tutors/${data.userId}/availability`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        const response = await res.json()

        return {
            ...response,
            availability: mapTutorAvailability(response.availability ?? {}),
        }
    }

    async getStudentRegularSchedule(studentId: string): Promise<IGetStudentRegularScheduleResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/schedules/students/${studentId}/regular`)

        return await res.json()
    }

    async previewStudentRegularSchedule(data: IPreviewStudentRegularScheduleRequest): Promise<IPreviewStudentRegularScheduleResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/schedules/students/${data.studentId}/regular/preview`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }

    async updateStudentRegularSchedule(data: IUpdateStudentRegularScheduleRequest): Promise<IUpdateStudentRegularScheduleResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/schedules/students/${data.studentId}/regular`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }

    async deleteStudentRegularSchedule(data: IDeleteStudentRegularScheduleRequest): Promise<IDeleteStudentRegularScheduleResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/schedules/students/${data.studentId}/regular/delete`, {
            method: 'POST',
            body: JSON.stringify(data),
        })

        return await res.json()
    }
}

export const scheduleService = new ScheduleService()
